import { array, hash } from '@ember/helper';
import { service } from '@ember/service';

import { includes } from '@nullvoxpopuli/ember-composable-helpers';
import CpPanel from 'ember-collapsible-panel/components/cp-panel/component';
import { t } from 'ember-intl';
import { trackedFunction } from 'reactiveweb/function';

import Component from 'umg-ember-backend/components/record-detail/forms';
import AssetPreview from 'umg-ember-backend/components/ui/asset-preview';
import Form from 'umg-ember-backend/components/ui/form';
import InfoBox from 'umg-ember-backend/components/ui/info-box';
import { labelValueOptions } from 'umg-ember-backend/utils/intl';

export default class RecordDetailFormsProductComponent extends Component {
  // Services
  @service data;
  @service intl;

  // Defaults
  externalTypes = [
    'amazon_download',
    'amazon_physical',
    'amazon_unlimited',
    'barnes_noble',
    'd2c_group_id',
    'deezer',
    'itunes',
    'jpc',
    'la_boite_a_musique',
    'mediamarkt_collection',
    'mediamarkt',
    'presto_digital',
    'presto_physical',
    'saturn_collection',
    'saturn',
    'shopify',
    'spotify',
  ];

  // Getter and setter
  get dvdFskCategories() {
    return this.resolvedDvdFskCategories.value ?? [];
  }

  get dvdRatioCategories() {
    return this.resolvedDvdRatioCategories.value ?? [];
  }

  get dvdCountryCategories() {
    return this.resolvedDvdCountryCategories.value ?? [];
  }

  get dvdSystemCategories() {
    return this.resolvedDvdSystemCategories.value ?? [];
  }

  get productSubCategories() {
    return this.resolvedProductSubCategories.value ?? [];
  }

  get productDeliveryStateCategories() {
    return this.resolvedProductDeliveryStateCategories.value ?? [];
  }

  get productGroupCategories() {
    return this.resolvedProductGroupCategories.value ?? [];
  }

  get shopCategories() {
    return this.resolvedShopCategories.value ?? [];
  }

  get externalTypeOptions() {
    return labelValueOptions.call(this, this.externalTypes, [
      'forms.external-value.options.externalType',
    ]);
  }

  // Functions
  reorderProductElements = (elements) => {
    elements.forEach((element, index) => {
      element.order = index + 1;
    });

    this.args.model.productElements = elements;
  };

  getExternelTypeHint = (type) => {
    const partner = this.externalTypeOptions.find(
      (option) => option.value == type,
    );

    switch (type) {
      case 'mediamarkt':
      case 'mediamarkt_collection':
      case 'saturn':
      case 'saturn_collection':
        return this.intl.t(
          'record_detail.forms.product.external_type_affiliate_hint',
          {
            partner: partner.label,
            htmlSafe: true,
          },
        );
      case 'spotify':
        return this.intl.t(
          'record_detail.forms.product.external_type_spotify_hint',
          { htmlSafe: true },
        );
      case 'itunes':
        return this.intl.t(
          'record_detail.forms.product.external_type_itunes_hint',
        );
      default:
        return null;
    }
  };

  resolvedDvdFskCategories = trackedFunction(this, async () => {
    return await this.data.getCategories({ 'filter[type]': 'FSK' });
  });

  resolvedDvdRatioCategories = trackedFunction(this, async () => {
    return await this.data.getCategories({ 'filter[type]': 'VIDEOFORMAT' });
  });

  resolvedDvdCountryCategories = trackedFunction(this, async () => {
    return await this.data.getCategories({ 'filter[type]': 'DVDCOUNTRYCODE' });
  });

  resolvedDvdSystemCategories = trackedFunction(this, async () => {
    return await this.data.getCategories({ 'filter[type]': 'VIDEOSTANDARD' });
  });

  resolvedProductSubCategories = trackedFunction(this, async () => {
    return await this.data.getCategories({
      'filter[type]': 'PRODUCTSUBCATEGORY',
    });
  });

  resolvedProductDeliveryStateCategories = trackedFunction(this, async () => {
    return await this.data.getCategories({ 'filter[type]': 'DELIVERYSTATE' });
  });

  resolvedProductGroupCategories = trackedFunction(this, async () => {
    return await this.data.getCategories({ 'filter[type]': 'PRODUCTGROUP' });
  });

  resolvedShopCategories = trackedFunction(this, async () => {
    return await this.data.getCategories({
      'filter[type]': 'SHOP',
      'filter[exclusiveForAudioplayers]': false,
    });
  });

  // Template
  <template>
    <Form @model={{@model}} @showErrors={{@showErrors}} as |form|>
      <div class='o-layout'>
        <div class='o-layout__item u-1/1 u-1/2@tablet'>
          <form.sections.variant
            @availableLanguages={{this.availableLanguages}}
          />

          <form.fieldset @titleKey='general' as |fieldset|>
            <fieldset.field
              @property='cmsState'
              @type='select-value'
              @options={{this.cmsStateOptions}}
            />

            {{#if this.isParent}}
              <fieldset.field
                @property='cmsLanguageId'
                @type='select-value'
                @options={{this.availableLanguages}}
              />
            {{/if}}

            <fieldset.field @property='headline' />
            <fieldset.field @property='subheadline' />
            <fieldset.field @property='releaseDate' @type='date' />

            {{#if @model.showInternationalReleaseDate}}
              <fieldset.field
                @property='internationalReleaseDate'
                @type='date'
              />
            {{/if}}

            <fieldset.field @property='sku' />
            <fieldset.field @property='text' @type='text-editor' />

            <fieldset.field
              @filters={{hash assetFolderId='1'}}
              @property='assetId1'
              @options={{hash allowedFolder='100'}}
              @type='belongs-to'
              as |record|
            >
              <AssetPreview @asset={{record}} />
            </fieldset.field>

            <fieldset.field @property='country' />
          </form.fieldset>

          <form.fieldset @titleKey='productDetails' as |fieldset|>
            <fieldset.field
              @property='label'
              @type='belongs-to'
              @on={{hash openNewRecord=false openRecord=false}}
            />

            <fieldset.field
              @property='internationalLabel'
              @type='belongs-to'
              @disabled={{true}}
              @on={{hash openNewRecord=false openRecord=false}}
            />

            <fieldset.field @property='prizeDe' />
            <fieldset.field @disbaled={{true}} @property='productPhysId' />
            <fieldset.field @disabled={{true}} @property='mmcProductId' />
            <fieldset.field @property='voeTba' @type='checkbox' />
            <fieldset.field @property='compilation' @type='checkbox' />
            <fieldset.field @property='useProdElemConf' @type='checkbox' />
            <fieldset.field @property='hideTracks' @type='checkbox' />
            <fieldset.field @property='placedPrioritized' @type='checkbox' />
          </form.fieldset>

          <form.fieldset @titleKey='categories' as |fieldset|>

            {{#if @model.showAlphaInformation}}
              <InfoBox @type='warning'>
                <strong>{{t
                    'record_detail.forms.product.alphaInformation'
                  }}</strong><br /><br />
                {{#if @model.alphaInformationCarrierNumber}}
                  <strong>{{t
                      'record_detail.forms.product.alphaInformationCarrierNumber'
                    }}:</strong><br />{{@model.alphaInformationCarrierNumber}}
                {{/if}}

                {{#if @model.alphaInformationVersion}}
                  <br /><strong>{{t
                      'record_detail.forms.product.alphaInformationVersion'
                    }}:</strong><br />{{@model.alphaInformationVersion}}
                {{/if}}

                {{#if @model.alphaInformationProductOutfits}}
                  <br /><strong>{{t
                      'record_detail.forms.product.alphaInformationProductOutfits'
                    }}:</strong><br />{{@model.alphaInformationProductOutfits}}
                {{/if}}

                {{#if @model.alphaInformationProductOutfitNotes}}
                  <br /><strong>{{t
                      'record_detail.forms.product.alphaInformationProductOutfitNotes'
                    }}:</strong><br />
                  {{@model.alphaInformationProductOutfitNotes}}
                {{/if}}
              </InfoBox>
            {{/if}}

            <fieldset.field
              @filters={{hash type='CONFIGURATION'}}
              @property='productPhysCategoryId1'
              @type='belongs-to'
              @on={{hash openNewRecord=false openRecord=false}}
            />

            <fieldset.field
              @property='categoryId'
              @type='select-record'
              @options={{this.productGroupCategories}}
            />

            <fieldset.field
              @filters={{hash type='PRODUCTFAMILY'}}
              @property='categoryId1'
              @type='belongs-to'
              @on={{hash openNewRecord=false openRecord=false}}
            />

            <fieldset.field
              @filters={{hash type='PRODUCTCATEGORY'}}
              @property='categoryId2'
              @type='belongs-to'
              @on={{hash openNewRecord=false openRecord=false}}
            />

            <fieldset.field
              @property='categoryId3'
              @type='select-record'
              @options={{this.productSubCategories}}
            />

            <fieldset.field
              @property='categoryId4'
              @type='select-record'
              @options={{this.productDeliveryStateCategories}}
            />

            <fieldset.field
              @property='additionalCategories'
              @type='has-many'
              @on={{hash openNewRecord=false openRecord=false}}
            />
          </form.fieldset>

          <form.fieldset @titleKey='dvdInfo' as |fieldset|>
            <fieldset.field
              @property='dvdRatio'
              @type='select-record'
              @options={{this.dvdRatioCategories}}
            />

            <fieldset.field
              @property='dvdSystem'
              @type='select-record'
              @options={{this.dvdSystemCategories}}
            />

            <fieldset.field
              @property='dvdCountryCode'
              @type='select-record'
              @options={{this.dvdCountryCategories}}
            />

            <fieldset.field
              @property='dvdFsk'
              @type='select-record'
              @options={{this.dvdFskCategories}}
            />

            <fieldset.field @property='dvdDuration' @type='number' />
            <fieldset.field @property='dvdBonus' @type='textarea' />
          </form.fieldset>

          <form.fieldset @titleKey='keywords' as |fieldset|>
            <fieldset.field @property='keywords' />
          </form.fieldset>

          <form.sections.seo />
        </div>
        <div class='o-layout__item u-1/1 u-1/2@tablet'>
          <form.fieldset @titleKey='contributors' as |fieldset|>
            <fieldset.field
              @property='contributors'
              @type='has-many-join'
              @hasManyForeignModelName='artist'
              @on={{hash openNewRecord=false removeRecord=this.deleteRelation}}
              as |contributor field|
            >
              <CpPanel @open={{contributor.isNew}} as |panel|>
                <panel.toggle>
                  {{contributor.artist.displayLabel}}
                  {{#if contributor.category}}
                    -
                    {{contributor.category.name}}
                  {{/if}}
                </panel.toggle>

                <panel.body>
                  <Form
                    @model={{contributor}}
                    @parentModel={{form.model}}
                    @showErrors={{@showErrors}}
                    as |subform|
                  >
                    <subform.field
                      @property='category'
                      @type='select-record'
                      @isSearchEnabled={{true}}
                      @options={{this.data.contributorCategories}}
                      @disabled={{field.isDisabled}}
                    />
                  </Form>
                </panel.body>
              </CpPanel>
            </fieldset.field>
          </form.fieldset>

          <form.fieldset @titleKey='shops' as |fieldset|>
            <fieldset.field
              @property='externalValues'
              @type='has-many'
              @hasManySortable={{false}}
              @on={{hash
                openNewRecord=this.newRecord
                removeRecord=this.deleteRelation
                openRecord=false
                openRecordList=false
              }}
              as |externalValue field|
            >
              <CpPanel @open={{externalValue.isNew}} as |panel|>
                <panel.toggle>
                  {{externalValue.externalType}}
                  ({{externalValue.externalId}})
                </panel.toggle>

                <panel.body>
                  <Form
                    @model={{externalValue}}
                    @parentModel={{form.model}}
                    @showErrors={{@showErrors}}
                    as |subform|
                  >
                    <subform.field
                      @property='externalType'
                      @type='select-value'
                      @isSearchEnabled={{true}}
                      @searchField='label'
                      @options={{this.externalTypeOptions}}
                      @disabled={{field.isDisabled}}
                      @hint={{this.getExternelTypeHint
                        externalValue.externalType
                      }}
                    />

                    <subform.field
                      @property='externalId'
                      @disabled={{field.isDisabled}}
                    />

                    <subform.field
                      @property='preOrderDate'
                      @type='date'
                      @disabled={{field.isDisabled}}
                    />
                  </Form>
                </panel.body>
              </CpPanel>
            </fieldset.field>

            <fieldset.field
              @property='customShops'
              @type='has-many'
              @on={{hash
                openNewRecord=this.newRecord
                removeRecord=this.deleteRelation
                openRecord=false
                openRecordList=false
              }}
              as |customShop field|
            >
              <CpPanel @open={{customShop.isNew}} as |panel|>
                <panel.toggle>
                  {{customShop.category.name}}
                </panel.toggle>

                <panel.body>
                  <Form
                    @model={{customShop}}
                    @parentModel={{form.model}}
                    @showErrors={{@showErrors}}
                    as |subform|
                  >
                    <subform.field
                      @property='category'
                      @type='select-record'
                      @isSearchEnabled={{true}}
                      @searchField='name'
                      @options={{this.shopCategories}}
                      @disabled={{field.isDisabled}}
                    />
                    {{#if
                      (includes
                        customShop.category.id
                        (array
                          '3280'
                          '3299'
                          '3300'
                          '3301'
                          '3302'
                          '3303'
                          '3332'
                          '3737'
                          '3739'
                          '3846'
                          '3885'
                          '4342'
                          '4621'
                        )
                      )
                    }}
                      <InfoBox @type='warning'>
                        {{t
                          'record_detail.forms.product.custom_shop_hint'
                          partner=customShop.category.name
                          htmlSafe=true
                        }}
                      </InfoBox>
                    {{/if}}
                    <subform.field
                      @property='url'
                      @disabled={{field.isDisabled}}
                    />
                    <subform.field
                      @property='partnerType'
                      @type='select-value'
                      @options={{this.data.partnerTypes}}
                      @disabled={{field.isDisabled}}
                    />
                  </Form>
                </panel.body>
              </CpPanel>
            </fieldset.field>
          </form.fieldset>

          <form.fieldset @titleKey='assignments' as |fieldset|>
            <fieldset.field
              @filters={{hash type='SUBGENRE'}}
              @property='categories'
              @type='has-many'
              @on={{hash openNewRecord=false}}
            />

            <fieldset.field @property='articles' @type='has-many' />

            <fieldset.field
              @filters={{hash assetFolderId='11'}}
              @property='videos'
              @type='has-many'
              as |record|
            >
              <AssetPreview @asset={{record}} />
            </fieldset.field>

            <fieldset.field
              @property='ownedByChannel'
              @type='belongs-to'
              @on={{hash openNewRecord=false openRecord=false}}
            />

            <fieldset.field
              @property='channelsAdditive'
              @type='has-many'
              @on={{hash openNewRecord=false}}
            />

            <fieldset.field
              @property='channelsExcluded'
              @type='has-many'
              @on={{hash openNewRecord=false}}
            />

            <fieldset.field
              @property='channelsExclusive'
              @type='has-many'
              @on={{hash openNewRecord=false}}
            />

            <fieldset.field
              @property='productElements'
              @type='has-many'
              @hasManyReorderItems={{this.reorderProductElements}}
              @hasManySortableColumn='order'
              @on={{hash openRecordList=false}}
            />

            <fieldset.field
              @property='recommendedTracks'
              @type='has-many'
              @on={{hash openNewRecord=false}}
            />

            <fieldset.field
              @property='shortUrls'
              @type='has-many'
              @on={{hash openRecordList=false}}
            />

            <fieldset.field
              @property='recommendedProducts'
              @type='has-many'
              @on={{hash openNewRecord=false}}
            />
          </form.fieldset>

          <form.fieldset @titleKey='taxonomies' as |fieldset|>
            <fieldset.field @property='taxa' @type='has-many' />
          </form.fieldset>

          <form.fieldset @titleKey='teaser' as |fieldset|>
            <fieldset.field @property='teaserHeadline' />
            <fieldset.field @property='teaserSubHeadline' />
            <fieldset.field @property='teaserText' @type='text-editor' />

            <fieldset.field
              @filters={{hash assetFolderId='10'}}
              @property='assetId'
              @options={{hash allowedFolder='100'}}
              @type='belongs-to'
              as |record|
            >
              <AssetPreview @asset={{record}} />
            </fieldset.field>
          </form.fieldset>

          <form.fieldset @titleKey='embargos' as |fieldset|>
            <fieldset.field
              @disabled={{true}}
              @property='coverEmbargo'
              @type='checkbox'
            />

            <fieldset.field
              @disabled={{true}}
              @property='trackEmbargo'
              @type='checkbox'
            />

            <fieldset.field
              @disabled={{true}}
              @property='soundEmbargo'
              @type='checkbox'
            />
          </form.fieldset>

          <form.sections.meta />
        </div>
      </div>
    </Form>
  </template>
}
